import React, { useEffect, useState } from "react"
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "src/components/ui/table"
import { Loader2 } from "lucide-react"
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts"
import { useToast } from "src/components/ui/toast"
import { fetchInventory, exportUrl } from "src/features/analytics/api/analyticsApi"
import { useReport } from "src/features/analytics/hooks/useReport"
import { presetToRange, CHART_COLORS, formatNumber } from "src/features/analytics/constants"
import ReportFilters from "src/features/analytics/components/ReportFilters"
import KpiCard from "src/features/analytics/components/KpiCard"
import AbsorptionChart from "src/features/analytics/components/AbsorptionChart"
import EmptyReportState from "src/features/analytics/components/EmptyReportState"

export default function InventoryReportPage() {
  const { toast } = useToast()
  const [preset, setPreset] = useState("90d")
  const [filters, setFilters] = useState(presetToRange("90d"))
  const { data, loading, fetch } = useReport(fetchInventory)

  useEffect(() => {
    fetch(filters).catch((err) => {
      if (err?.response?.status === 403) {
        toast({ variant: "destructive", title: "Error", description: "You don't have permission to view reports." })
      }
    })
  }, [filters.from, filters.to, filters.project_id])

  const totals = data?.totals

  return (
    <div className="mx-auto max-w-[1120px]">
      <ReportFilters
        filters={filters}
        onFiltersChange={setFilters}
        preset={preset}
        onPresetChange={setPreset}
        exportUrl={exportUrl("inventory", filters)}
      />

      {loading ? (
        <div className="flex items-center gap-2 p-8 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading…
        </div>
      ) : !totals || totals.total === 0 ? (
        <EmptyReportState message="No inventory data. Add units to your projects to see absorption metrics." />
      ) : (
        <>
          <div className="mb-5 grid grid-cols-2 gap-3 lg:grid-cols-5">
            <KpiCard label="Total Units" value={formatNumber(totals.total)} />
            <KpiCard label="Available" value={formatNumber(totals.available)} />
            <KpiCard label="Booked" value={formatNumber(totals.booked)} />
            <KpiCard label="Sold" value={formatNumber(totals.sold)} />
            <KpiCard
              label="Absorption"
              value={`${data.absorption_rate_pct}%`}
              sublabel={data.months_to_sell_out != null ? `~${data.months_to_sell_out} months to sell out` : undefined}
            />
          </div>

          <div className="mb-5 grid grid-cols-1 gap-5 lg:grid-cols-2">
            <KpiCard
              label="Monthly Velocity"
              value={data.monthly_velocity}
              suffix="units/month"
            />
            <KpiCard
              label="Months to Sell Out"
              value={data.months_to_sell_out ?? "∞"}
              suffix="months"
            />
          </div>

          <div className="mb-5">
            <AbsorptionChart byProject={data.by_project} />
          </div>

          {/* Absorption trend */}
          {data.trend?.length > 0 && (
            <div className="mb-5 rounded-lg border border-slate-200 bg-white p-4">
              <div className="mb-3 text-[13px] font-medium text-slate-700">Monthly Absorption</div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={data.trend}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eef2f6" />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 12 }} />
                  <Tooltip />
                  <Bar dataKey="units_absorbed" name="Units absorbed" fill={CHART_COLORS.primary} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* By config */}
          {data.by_config?.length > 0 && (
            <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
              <div className="px-4 py-3 text-[13px] font-medium text-slate-700">By Configuration (Demand Mix)</div>
              <Table className="text-[13px]">
                <TableHeader className="bg-slate-50 text-left text-slate-500">
                  <TableRow className="hover:bg-transparent">
                    <TableHead className="h-9 font-medium text-slate-500">Configuration</TableHead>
                    <TableHead className="h-9 text-right font-medium text-slate-500">Total</TableHead>
                    <TableHead className="h-9 text-right font-medium text-slate-500">Sold</TableHead>
                    <TableHead className="h-9 text-right font-medium text-slate-500">Available</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.by_config.map((row) => (
                    <TableRow key={row.configuration}>
                      <TableCell className="py-2.5 text-slate-900">{row.configuration}</TableCell>
                      <TableCell className="py-2.5 text-right">{row.total}</TableCell>
                      <TableCell className="py-2.5 text-right">{row.sold}</TableCell>
                      <TableCell className="py-2.5 text-right">{row.available}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </>
      )}
    </div>
  )
}
